import { html, unsafeCSS } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_GENERAL_ADVANCED } from '../../../../../../../webcomponents.constants';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import {
  GeneralAdvancedFormValue,
  GeneralAdvancedSearchableKeywords,
} from '../../../../utils/uiSettings/general/general.types';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { ModuleId, PasswordConfigProfile, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import {
  GENERAL_ADVANCED_SECTION_DEFAULT_VALUE,
  getGeneralAdvancedDefaultValue,
  getGeneralRemoveEventsOlderThanOptions,
  getGeneralAdvancedSearchableKeywords,
} from '../../../../utils/uiSettings/general/general.values';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../ui/gzInput';
import { InputTypes } from 'apps/webcomponents/src/ui/gzInput/types';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';
import { GzInputWebComponent } from '../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';
import { CustomizableValidator } from '../../../../../../../ui/gzForm/validators/customizable.validator';
import { validatePasswordComplexity } from '../../../../../../common/policyPanel/uiSettings/utils/validators/credentials.validators';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { GzDialogWebComponent } from '../../../../../../../ui/gzDialog';
import { GzDialogEvents } from '../../../../../../../ui/gzDialog/component/GzDialog';
import { GzButtonWebComponent } from '../../../../../../../ui/gzButton';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { ProxyServerValidator } from '../../../../../../../ui/gzForm/validators/proxy-server.validator';
import { GzHardCodedFormField } from '../../../../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { classMap } from 'lit/directives/class-map.js';
import { Feature } from '@nirvana/shared/constants';

export const MIN_PORT_VALUE = 1;
export const MAX_PORT_VALUE = 65535;

@customElement(WEB_COMPONENT_POLICY_PANEL_GENERAL_ADVANCED)
export class GzGeneralAdvancedWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  GeneralAdvancedFormValue,
  GeneralAdvancedSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.GENERAL,
    sectionId: SectionId.SETTINGS,
  };

  protected searchableKeywords = getGeneralAdvancedSearchableKeywords();

  @query('#proxy-configuration-form')
  private proxyConfigurationForm: HTMLElement;
  @query('#power-user-form')
  private powerUserForm: HTMLElement;
  @query('#uninstall-password-config')
  private uninstallPasswordConfigElement: HTMLElement;
  @query('#password-profile-form-field')
  private passwordProfileForm: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
  @query('#uninstall-password-config-field')
  private uninstallPasswordConfigField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#uninstall-password-config-retype-field')
  private uninstallPasswordConfigRetypeField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#general-proxy-configuration-toggle')
  private proxyToggleField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#proxy-server-field')
  private proxyServerField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#proxy-port-field')
  private proxyPortField: GzFormFieldWebComponent<GzInputNumberWebComponent, number | undefined>;
  @query('#proxy-username-field')
  private proxyUsernameField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#proxy-password-field')
  private proxyPasswordField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#general-power-user-toggle')
  private powerUserToggleField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#powerUser-password-field')
  private powerUserPasswordField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#powerUser-password-retype-field')
  private powerUserPasswordRetypeField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#remove-events-field')
  private removeEventsField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
  @query('#reportActiveSessions-field')
  private reportActiveSessionsField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#sendTelemetry-field')
  private sendTelemetryField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#submitReports-field')
  private submitReportsField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#submitSuspicious-field')
  private submitSuspiciousField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#useGlobalProtectiveNetwork-field')
  private useGlobalProtectiveNetworkField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  protected createFormGroup(): void {
    this.formGroup = FormGroup.build({
      uiSettings: {
        general: {
          advanced: {
            passwordConfig: {
              profile: this.passwordProfileForm,
              value: this.uninstallPasswordConfigField,
            },
            powerUser: {
              enabled: this.powerUserToggleField,
              password: this.powerUserPasswordField,
            },
            settings: {
              removeEvents: {
                settings: {
                  days: this.removeEventsField,
                },
              },
              reportActiveSessions: this.reportActiveSessionsField,
              sendTelemetry: this.sendTelemetryField,
              submitReports: this.submitReportsField,
              submitSuspicious: this.submitSuspiciousField,
              useGlobalProtectiveNetwork: this.useGlobalProtectiveNetworkField,
            },
          },
          update: {
            settings: {
              proxy: {
                enable: this.proxyToggleField,
                profile: new GzHardCodedFormField<number>(1),
                settings: {
                  server: this.proxyServerField,
                  port: this.proxyPortField,
                  username: this.proxyUsernameField,
                  password: this.proxyPasswordField,
                },
              },
            },
          },
        },
      },
    });
  }

  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    return this.formGroup.value;
  }

  protected setValidators() {
    const proxyUsernameCustomValidator = [
      CustomizableValidator(() => {
        const password = this.proxyPasswordField.value;
        if (password && !this.proxyUsernameField.value) {
          return { valid: false, error: translateFn('Enter username') };
        }
        return { valid: true };
      }),
    ];

    this.proxyPortField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.proxyPortField.validate(true);

    this.proxyUsernameField.Validators = proxyUsernameCustomValidator;
    this.proxyUsernameField.validate(true);

    this.proxyPortField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.proxyPortField.validate(true);
    this.proxyServerField.Validators = [
      RequiredValidator(translateFn('Required field')),
      ProxyServerValidator('Invalid server address'),
    ];
    this.proxyServerField.validate(true);
  }

  protected getValueFromManager(): GeneralAdvancedFormValue {
    const defaultValue = {
      uiSettings: {
        [ModuleId.GENERAL]: {
          advanced: {
            passwordConfig: GENERAL_ADVANCED_SECTION_DEFAULT_VALUE.passwordConfig,
            powerUser: GENERAL_ADVANCED_SECTION_DEFAULT_VALUE.powerUser,
            settings: {
              removeEvents: GENERAL_ADVANCED_SECTION_DEFAULT_VALUE.settings.removeEvents,
              reportActiveSessions: GENERAL_ADVANCED_SECTION_DEFAULT_VALUE.settings.reportActiveSessions,
              sendTelemetry: GENERAL_ADVANCED_SECTION_DEFAULT_VALUE.settings.sendTelemetry,
              submitReports: GENERAL_ADVANCED_SECTION_DEFAULT_VALUE.settings.submitReports,
              submitSuspicious: GENERAL_ADVANCED_SECTION_DEFAULT_VALUE.settings.submitSuspicious,
              useGlobalProtectiveNetwork: GENERAL_ADVANCED_SECTION_DEFAULT_VALUE.settings.useGlobalProtectiveNetwork,
            },
          },
          update: {
            settings: {
              proxy: getGeneralAdvancedDefaultValue().uiSettings.general.update.settings.proxy,
            },
          },
        },
      },
    };
    const value = {
      uiSettings: {
        general: {
          advanced: {
            passwordConfig: PolicyPanelManager.getInstance().getPolicy('uiSettings.general.advanced.passwordConfig'),
            powerUser: PolicyPanelManager.getInstance().getPolicy('uiSettings.general.advanced.powerUser'),
            settings: {
              removeEvents: PolicyPanelManager.getInstance().getPolicy(
                'uiSettings.general.advanced.settings.removeEvents'
              ),
              reportActiveSessions: PolicyPanelManager.getInstance().getPolicy(
                'uiSettings.general.advanced.settings.reportActiveSessions',
                true
              ),
              sendTelemetry: PolicyPanelManager.getInstance().getPolicy(
                'uiSettings.general.advanced.settings.sendTelemetry',
                true
              ),
              submitReports: PolicyPanelManager.getInstance().getPolicy(
                'uiSettings.general.advanced.settings.submitReports',
                true
              ),
              submitSuspicious: PolicyPanelManager.getInstance().getPolicy(
                'uiSettings.general.advanced.settings.submitSuspicious',
                true
              ),
              useGlobalProtectiveNetwork: PolicyPanelManager.getInstance().getPolicy(
                'uiSettings.general.advanced.settings.useGlobalProtectiveNetwork',
                true
              ),
            },
          },
          update: {
            settings: {
              proxy: PolicyPanelManager.getInstance().getPolicy('uiSettings.general.update.settings.proxy'),
            },
          },
        },
      },
    };

    return deepmerge(defaultValue, value);
  }

  protected renderContent() {
    return html`<div id="general-advanced-content">
      ${this.renderUninstallPasswordConfiguration()} ${this.renderProxyConfiguration()} ${this.renderPowerUser()}
      ${this.renderAdvancedOptions()}
    </div>`;
  }

  protected async setUninstallPasswordValidators(disabled: boolean) {
    const uninstallPasswordConfigMatchValidator = [
      CustomizableValidator(() => {
        const password = this.uninstallPasswordConfigField.value;
        const confirmation = this.uninstallPasswordConfigRetypeField.value;

        return password === confirmation
          ? { valid: true }
          : { valid: false, error: translateFn('Passwords do not match.') };
      }),
    ];

    this.uninstallPasswordConfigField.Validators = disabled
      ? []
      : [
          RequiredValidator('Required field'),
          CustomizableValidator(validatePasswordComplexity),
          ...uninstallPasswordConfigMatchValidator,
        ];

    this.uninstallPasswordConfigRetypeField.Validators = disabled
      ? []
      : [RequiredValidator(translateFn('Required field')), ...uninstallPasswordConfigMatchValidator];

    this.uninstallPasswordConfigRetypeField.validate(true);
    this.uninstallPasswordConfigField.validate(true);
  }

  protected setPowerUserPasswordValidators(disabled: boolean) {
    const powerUserPasswordMatchValidator = [
      CustomizableValidator(() => {
        const password = this.powerUserPasswordField.value;
        const confirmation = this.powerUserPasswordRetypeField.value;

        return password === confirmation
          ? { valid: true }
          : { valid: false, error: translateFn('Passwords do not match.') };
      }),
    ];

    this.powerUserPasswordField.Validators = disabled
      ? []
      : [
          RequiredValidator('Required field'),
          CustomizableValidator(validatePasswordComplexity),
          ...powerUserPasswordMatchValidator,
        ];

    this.powerUserPasswordRetypeField.Validators = disabled
      ? []
      : [RequiredValidator(translateFn('Required field')), ...powerUserPasswordMatchValidator];

    this.powerUserPasswordField.validate(true);
    this.powerUserPasswordRetypeField.validate(true);
  }

  private renderUninstallPasswordConfiguration() {
    return html`<div id="uninstall-password-configuration" class="mt-4">
      <gz-title
        class="form-subtitle"
        searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.UninstallPasswordConfiguration]
          .label}"
        fontWeight="${FontWeights.SEMIBOLD}"
        fontSize="${FontSizes.PARAGRAPH_11}"
      >
        ${this.searchableKeywords[GeneralAdvancedSearchableKeywords.UninstallPasswordConfiguration].translatedLabel}
      </gz-title>
      <gz-form-field id="password-profile-form-field" class="mt-2">
        <gz-input-error></gz-input-error>
        <gz-radio-group
          gz-form-input
          @radioSelectionChanged=${this.handleUninstallPasswordConfigurationSelectionChange.bind(this)}
        >
          <gz-radio
            .value="${PasswordConfigProfile.KEEP_CURRENT_SETTINGS}"
            label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.KeepInstallationSettings]
              .translatedLabel}"
            searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.KeepInstallationSettings]
              .label}"
          >
          </gz-radio>
          <gz-radio
            .value="${PasswordConfigProfile.ENABLE_PASSWORD}"
            label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.SetUninstallPassword].translatedLabel}"
            searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.SetUninstallPassword].label}"
          >
          </gz-radio>
          <div id="uninstall-password-config">
            <gz-text
              id="password-profile-enable-password-text"
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="helper-text mb-2"
              >${translateFn(
                'Setting an uninstall password requires users to enter this password to uninstall the security agent, even if they have local administrator rights.'
              )}</gz-text
            >
            <gz-form-field
              id="uninstall-password-config-field"
              class="label-mw-224 form-field-margin margin-spacing"
              label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Password].translatedLabel}"
              searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Password].label}"
            >
              <gz-input
                gz-form-input
                inputWidth="${InputWidthValues.LARGE}"
                type="${InputTypes.PASSWORD}"
                @change="${e => this.uninstallPasswordConfigRetypeField.validate(true)}"
              >
              </gz-input>
              <gz-input-error></gz-input-error>
            </gz-form-field>
            <gz-form-field
              class="label-mw-224 margin-spacing"
              id="uninstall-password-config-retype-field"
              label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.RetypePassword].translatedLabel}"
              searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.RetypePassword].label}"
            >
              <gz-input
                gz-form-input
                inputWidth="${InputWidthValues.LARGE}"
                type="${InputTypes.PASSWORD}"
                @change="${e => this.uninstallPasswordConfigField.validate(true)}"
              ></gz-input>
              <gz-input-error></gz-input-error>
              <gz-text
                fontSize="${FontSizes.PARAGRAPH_12}"
                id="uninstall-password-config-retype-text"
                class="password-info"
                fontWeight="${FontWeights.NORMAL}"
                >${translateFn(
                  'The password must be at least 6 characters and contain at least one digit, one uppercase letter, one lowercase letter, and one special character.'
                )}</gz-text
              >
            </gz-form-field>
          </div>
          <gz-radio
            .value="${PasswordConfigProfile.DISABLE_PASSWORD}"
            label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.DisablePassword].translatedLabel}"
            searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.DisablePassword].label}"
          >
          </gz-radio>
        </gz-radio-group>
      </gz-form-field>
    </div>`;
  }

  private renderProxyConfiguration() {
    return html`
      <div id="proxy-configuration-section" class="mt-4">
        <gz-form-field id="general-proxy-configuration-toggle" class="mbw-1">
          <gz-toggle
            class="display-flex"
            searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.ProxyConfiguration].label}"
            gz-form-input
            @toggleChange="${this.onProxyConfigurationToggleChange}"
          >
            <gz-title
              class="toggle-form-subtitle"
              fontSize="${FontSizes.PARAGRAPH_11}"
              fontWeight="${FontWeights.SEMIBOLD}"
              >${this.searchableKeywords[GeneralAdvancedSearchableKeywords.ProxyConfiguration].translatedLabel}
            </gz-title>
          </gz-toggle>
        </gz-form-field>
        <div id="proxy-configuration-form" class="mt-2">
          <gz-form-field
            class="label-mw-250 form-field-margin"
            id="proxy-server-field"
            label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Server].translatedLabel}"
            searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Server].label}"
          >
            <gz-input gz-form-input inputWidth="${InputWidthValues.LARGE}"></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>
          <gz-form-field
            class="form-field-margin"
            id="proxy-port-field"
            label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Port].translatedLabel}"
            searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Port].label}"
          >
            <gz-input-number
              gz-form-input
              inputWidth="${InputWidthValues.LARGE}"
              min="${MIN_PORT_VALUE}"
              max="${MAX_PORT_VALUE}"
            ></gz-input-number>
            <gz-input-error></gz-input-error>
          </gz-form-field>
          <gz-form-field
            class="label-mw-250 form-field-margin"
            id="proxy-username-field"
            label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Username].translatedLabel}"
            searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Username].label}"
          >
            <gz-input gz-form-input inputWidth="${InputWidthValues.LARGE}"></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>
          <gz-form-field
            class="label-mw-250"
            id="proxy-password-field"
            label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Password].translatedLabel}"
            searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Password].label}"
          >
            <gz-input
              gz-form-input
              inputWidth="${InputWidthValues.LARGE}"
              type="${InputTypes.PASSWORD}"
              @change="${this.handlePasswordChanged}"
            >
            </gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>
        </div>
      </div>
    `;
  }

  private renderPowerUser() {
    return html` <div
      id="power-user-section"
      class="${classMap({
        hidden: Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]),
      })} mt-4"
    >
      <gz-form-field id="general-power-user-toggle" class="mbw-1">
        <gz-toggle
          class="display-flex"
          searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.PowerUser].label}"
          gz-form-input
          @toggleChange="${this.onPowerUserToggleChange}"
        >
          <gz-title
            class="toggle-form-subtitle"
            fontSize="${FontSizes.PARAGRAPH_11}"
            fontWeight="${FontWeights.SEMIBOLD}"
            >${this.searchableKeywords[GeneralAdvancedSearchableKeywords.PowerUser].translatedLabel}</gz-title
          >
        </gz-toggle>
      </gz-form-field>
      <div id="power-user-form" class="mt-2">
        <gz-form-field
          class="form-field-margin margin-spacing label-mw-224"
          id="powerUser-password-field"
          label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Password].translatedLabel}"
          searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Password].label}"
        >
          <gz-input
            gz-form-input
            inputWidth="${InputWidthValues.LARGE}"
            type="${InputTypes.PASSWORD}"
            @change="${e => this.powerUserPasswordRetypeField.validate(true)}"
          >
          </gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>
        <gz-form-field
          class="margin-spacing label-mw-224"
          id="powerUser-password-retype-field"
          label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.RetypePassword].translatedLabel}"
          searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.RetypePassword].label}"
        >
          <gz-input
            gz-form-input
            inputWidth="${InputWidthValues.LARGE}"
            type="${InputTypes.PASSWORD}"
            @change="${e => this.powerUserPasswordField.validate(true)}"
          ></gz-input>
          <gz-input-error></gz-input-error>
          <gz-text
            fontSize="${FontSizes.PARAGRAPH_12}"
            class="password-info"
            id="powerUser-password-retype-text"
            fontWeight="${FontWeights.NORMAL}"
            >${translateFn(
              'The password must be at least 6 characters and contain at least one digit, one uppercase letter, one lowercase letter, and one special character.'
            )}</gz-text
          >
        </gz-form-field>
      </div>
    </div>`;
  }

  private renderAdvancedOptions() {
    return html`<div id="general-advanced-options" class="mt-4">
      <gz-title
        class="form-subtitle"
        searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Options].label}"
        fontWeight="${FontWeights.SEMIBOLD}"
        fontSize="${FontSizes.PARAGRAPH_13}"
      >
        ${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Options].translatedLabel}
      </gz-title>
      <gz-form-field
        class="label-mw-250 mt-2"
        id="remove-events-field"
        label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.RemoveEventsOlderThan].translatedLabel}"
        searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.RemoveEventsOlderThan].label}"
      >
        <gz-dropdown
          gz-form-input
          .options="${getGeneralRemoveEventsOlderThanOptions()}"
          labelWidth="${InputWidthValues.MEDIUM}"
        ></gz-dropdown>
      </gz-form-field>
      <gz-form-field class="label-mw-250 mt-2 form-field-checkbox-margin" id="submitReports-field">
        <gz-checkbox
          gz-form-input
          fontWeight="${FontWeights.NORMAL}"
          fontSize="${FontSizes.PARAGRAPH_13}"
          label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.SubmitCrashReportsToBitdefender]
            .translatedLabel}"
          searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.SubmitCrashReportsToBitdefender]
            .label}"
          @change="${this.onSubmitReportsCheckboxChange}"
        >
        </gz-checkbox>
      </gz-form-field>
      <gz-form-field class="label-mw-250 mbw-1" id="submitSuspicious-field">
        <gz-checkbox
          gz-form-input
          fontWeight="${FontWeights.NORMAL}"
          fontSize="${FontSizes.PARAGRAPH_13}"
          label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.SubmitSuspiciousFilesForAnalysis]
            .translatedLabel}"
          searchable-label="${this.searchableKeywords[
            GeneralAdvancedSearchableKeywords.SubmitSuspiciousFilesForAnalysis
          ].label}"
        >
        </gz-checkbox>
        <gz-text
          id="submitSuspicious-text"
          class="helper-text mt-1"
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.NORMAL}"
          >${translateFn('Changing this setting requires an endpoint restart.')}
        </gz-text>
      </gz-form-field>
      <gz-form-field class="label-mw-250 form-field-checkbox-margin" id="sendTelemetry-field">
        <gz-checkbox
          gz-form-input
          fontWeight="${FontWeights.NORMAL}"
          fontSize="${FontSizes.PARAGRAPH_13}"
          label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.SendFeedbackRegardingSecurityAgentsHealth]
            .translatedLabel}"
          searchable-label="${this.searchableKeywords[
            GeneralAdvancedSearchableKeywords.SendFeedbackRegardingSecurityAgentsHealth
          ].label}"
          @change="${this.onSendTelemetryCheckboxChange}"
        >
        </gz-checkbox>
      </gz-form-field>
      <gz-form-field class="label-mw-250 form-field-checkbox-margin" id="useGlobalProtectiveNetwork-field">
        <gz-checkbox
          gz-form-input
          fontWeight="${FontWeights.NORMAL}"
          fontSize="${FontSizes.PARAGRAPH_13}"
          label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.UseBitdefenderGlobalProtectiveNetwork]
            .translatedLabel}"
          searchable-label="${this.searchableKeywords[
            GeneralAdvancedSearchableKeywords.UseBitdefenderGlobalProtectiveNetwork
          ].label}"
          @change="${this.onUseGlobalProtectiveNetworkCheckboxChange}"
        >
        </gz-checkbox>
      </gz-form-field>
      <gz-form-field class="label-mw-250 mbw-1" id="reportActiveSessions-field">
        <gz-checkbox
          gz-form-input
          fontWeight="${FontWeights.NORMAL}"
          fontSize="${FontSizes.PARAGRAPH_13}"
          label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.AllowEndpointToSendUserLoginDataToGZ]
            .translatedLabel}"
          searchable-label="${this.searchableKeywords[
            GeneralAdvancedSearchableKeywords.AllowEndpointToSendUserLoginDataToGZ
          ].label}"
        >
        </gz-checkbox>
      </gz-form-field>
      <gz-text
        id="reportActiveSessions-text"
        class="helper-text"
        fontSize="${FontSizes.PARAGRAPH_12}"
        fontWeight="${FontWeights.NORMAL}"
        >${translateFn(
          'The endpoint will send to GravityZone the username, login time, and the method used to log in. This information is displayed in the Network grid, endpoint details page and Network Protection Status report. Disabling this option will not delete the information from the existing Network Protection Status reports, but it will no longer be displayed in Control Center.'
        )}
      </gz-text>
    </div>`;
  }

  private onSubmitReportsCheckboxChange(event: CustomEvent) {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    if (!event.detail.checked) {
      // open dialog get response
      const dialogContent = `<p>
          ${translateFn(
            'Disabling this option may prevent Bitdefender from proactively investigating the issue that caused the product crash.'
          )}
        </p>
        <p>${translateFn('It is recommended to keep this option enabled.')}</p>`;
      this.openDialog(translateFn('Disable crash report submission?'), dialogContent, this.submitReportsField);
    }
  }

  private onSendTelemetryCheckboxChange(event: CustomEvent) {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    if (!event.detail.checked) {
      // open dialog get response
      const dialogContent = `<p>
          ${translateFn(
            'The feedback contains solely Bitdefender product information, for the purpose of improving the services.'
          )}
        </p>
        <p>${translateFn(
          'Disabling this option may prevent Bitdefender from correctly identifying and solving the issues you may encounter with the security agents.'
        )}</p>`;
      this.openDialog(translateFn('Disable feedback submission?'), dialogContent, this.sendTelemetryField);
    }
  }

  private onUseGlobalProtectiveNetworkCheckboxChange(event: CustomEvent) {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    if (!event.detail.checked) {
      // open dialog get response
      const dialogContent = `<div>${translateFn(
        'Disabling this option will severely reduce Antimalware and Network Protection effectiveness.'
      )}
      <br>${translateFn('Additionally, if you use an XDR NSVA, the Network Vulnerability Scanner module will not function.')}
      <br>
      <br>${translateFn('You may experience:')}
      <ul><li>${translateFn('Increased false positives or negatives.')}</li>
      <li>${translateFn('Delayed zero-day attack detection.')}</li>
      <li>${translateFn('Reduced Hybrid Scan engine efficiency.')}</li>
      <li>${translateFn('Degraded web traffic filtering.')}</li>
      <li>${translateFn('Impact on other dependent modules.')}</li>
      <li>${translateFn('Reduced awareness of network vulnerabilities.')}</li>
      </ul>${translateFn('If using Hybrid Scan engines, you must switch to Local Scan engines.')}
      </div>`;
      this.openDialog(
        translateFn('Disable the option to use Bitdefender Global Protective Network?'),
        dialogContent,
        this.useGlobalProtectiveNetworkField
      );
    }
  }
  private openDialog(
    headlineText: string,
    dialogContent: string,
    checkbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>
  ) {
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = dialogContent;
    const dialogFooter = document.createElement('div');
    const confirmButton = new GzButtonWebComponent();
    const cancelButton = new GzButtonWebComponent();
    confirmButton.innerText = translateFn('Disable');
    cancelButton.innerText = translateFn('Cancel');
    cancelButton.setAttribute('type', 'outline');
    cancelButton.setAttribute('color', 'secondary');
    dialogFooter.appendChild(confirmButton);
    dialogFooter.appendChild(cancelButton);

    const dialogConfigs = {
      headline: headlineText,
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
    };
    const dialog = GzDialogWebComponent.buildDialogElement(
      dialogConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );

    dialog.addEventListener(GzDialogEvents.CANCEL, () => {
      checkbox.value = true;
    });

    confirmButton.addEventListener('click', () => {
      dialog.close();
    });
    cancelButton.addEventListener('click', () => {
      checkbox.value = true;
      dialog.close();
    });
  }

  protected async onProxyConfigurationToggleChange(event: CustomEvent) {
    await this.updateComplete;
    toggleDisabled(this.proxyConfigurationForm, !event.detail.checked);
  }
  protected async onPowerUserToggleChange(event: CustomEvent) {
    await this.updateComplete;

    toggleDisabled(this.powerUserForm, !event.detail.checked);
    this.setPowerUserPasswordValidators(!event.detail.checked);
  }

  protected async handleUninstallPasswordConfigurationSelectionChange(event: CustomEvent) {
    await this.updateComplete;

    const value = event.detail.selectedValue as PasswordConfigProfile;
    toggleDisabled(this.uninstallPasswordConfigElement, !(value === PasswordConfigProfile.ENABLE_PASSWORD));
    await this.setUninstallPasswordValidators(value !== PasswordConfigProfile.ENABLE_PASSWORD);
  }

  protected handlePasswordChanged() {
    this.proxyUsernameField.validate(true);
  }
  protected renderInfoText(): void {}

  protected renderTitle() {
    return html` <span>
      <gz-title
        searchable-label="${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Settings].label}"
        fontWeight="${FontWeights.MEDIUM}"
        fontSize="${FontSizes.TITLE}"
      >
        ${this.searchableKeywords[GeneralAdvancedSearchableKeywords.Settings].translatedLabel}
      </gz-title></span
    >`;
  }

  protected setFormData(): void {
    if (!this.valueT) {
      return;
    }

    this.formGroup.value = {
      uiSettings: {
        general: {
          advanced: {
            passwordConfig: this.valueT.uiSettings.general.advanced.passwordConfig,
            powerUser: this.valueT.uiSettings.general.advanced.powerUser,
            settings: {
              removeEvents: this.valueT.uiSettings.general.advanced.settings.removeEvents,
              reportActiveSessions: this.valueT.uiSettings.general.advanced.settings.reportActiveSessions,
              sendTelemetry: this.valueT.uiSettings.general.advanced.settings.sendTelemetry,
              submitReports: this.valueT.uiSettings.general.advanced.settings.submitReports,
              submitSuspicious: this.valueT.uiSettings.general.advanced.settings.submitSuspicious,
              useGlobalProtectiveNetwork: this.valueT.uiSettings.general.advanced.settings.useGlobalProtectiveNetwork,
            },
          },
          update: {
            settings: {
              proxy: this.valueT.uiSettings.general.update.settings.proxy,
            },
          },
        },
      },
    };
    this.powerUserPasswordRetypeField.value = this.valueT.uiSettings.general.advanced.powerUser.password;
    this.uninstallPasswordConfigRetypeField.value = this.valueT.uiSettings.general.advanced.passwordConfig.value;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_GENERAL_ADVANCED: GzGeneralAdvancedWebComponent;
  }
}
